import React, { useEffect, useState, useCallback } from "react";
import { api, PLATFORM_LABELS, STATUS_CONFIG, STATUS_FLOW } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CaseDetail } from "@/components/CaseDetail";
import { Search, ExternalLink, Trash2, CheckCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Cases() {
  const [cases, setCases] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("alle");
  const [platform, setPlatform] = useState("alle");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (platform !== "alle") params.platform = platform;
    const r = await api.get("/cases", { params });
    setCases(r.data);
  }, [q, platform]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (caseId, newStatus) => {
    await api.patch(`/cases/${caseId}/status`, { status: newStatus });
    toast.success(`Status aktualisiert: ${STATUS_CONFIG[newStatus].label}`);
    load();
  };

  const remove = async (caseId) => {
    await api.delete(`/cases/${caseId}`);
    toast.success("Fall gelöscht.");
    load();
  };

  const openCase = (c) => {
    setSelected(c);
    if (c.is_new) {
      api.post(`/cases/${c.id}/seen`);
      setCases((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_new: false } : x)));
    }
  };

  const markAllSeen = async () => {
    const r = await api.post("/cases/mark-all-seen");
    toast.success(`${r.data.updated} Treffer als gesehen markiert.`);
    load();
  };

  const newCount = cases.filter((c) => c.is_new).length;

  const tabs = ["alle", ...STATUS_FLOW];
  const countFor = (s) => (s === "alle" ? cases.length : cases.filter((c) => c.status === s).length);
  const visibleCases = status === "alle" ? cases : cases.filter((c) => c.status === status);

  return (
    <div className="space-y-6" data-testid="cases-page">
      <PageHeader title="Fallakte" subtitle="Alle erfassten Profile und ihr Bearbeitungsstatus">
        {newCount > 0 && (
          <Button
            data-testid="mark-all-seen-button"
            onClick={markAllSeen}
            variant="outline"
            className="rounded-md border-[#E0DCD6]"
          >
            <CheckCheck className="mr-2 h-4 w-4" /> {newCount} neue als gesehen
          </Button>
        )}
      </PageHeader>

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-4 shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A6A4A9]" />
            <Input
              data-testid="case-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, Handle oder Keyword suchen …"
              className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] pl-10 focus-visible:ring-[#0A0A0A]"
            />
          </div>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger data-testid="filter-platform" className="w-full rounded-md border-[#E0DCD6] sm:w-44">
              <SelectValue placeholder="Plattform" />
            </SelectTrigger>
            <SelectContent className="rounded-md">
              <SelectItem value="alle">Alle Plattformen</SelectItem>
              {Object.entries(PLATFORM_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2" data-testid="case-status-tabs">
        {tabs.map((s) => {
          const active = status === s;
          const label = s === "alle" ? "Alle" : STATUS_CONFIG[s].label;
          const count = countFor(s);
          return (
            <button
              key={s}
              data-testid={`case-tab-${s}`}
              onClick={() => setStatus(s)}
              className={`inline-flex items-center gap-2 rounded-md border px-3.5 py-2 text-sm font-semibold transition-colors ${
                active
                  ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                  : "border-[#E0DCD6] bg-white text-[#3A3A3C] hover:bg-[#FAF7F2]"
              }`}
            >
              {s !== "alle" && (
                <span className={`h-2 w-2 rounded-full ${STATUS_CONFIG[s].dot}`} />
              )}
              {label}
              <span
                className={`inline-flex min-w-[1.4rem] justify-center rounded-full px-1.5 py-0.5 text-xs font-bold tabular-nums ${
                  active ? "bg-white/20 text-white" : "bg-[#F4F1EC] text-[#8B8A8E]"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <Card className="overflow-hidden rounded-lg border-[#E8E4DF] bg-white shadow-[0_2px_16px_rgb(10,10,10,0.03)]">
        <Table>
          <TableHeader>
            <TableRow className="border-[#EDE9E3] hover:bg-transparent">
              <TableHead className="px-6 py-4 text-xs font-bold uppercase tracking-wide text-[#8B8A8E]">Profil</TableHead>
              <TableHead className="hidden text-xs font-bold uppercase tracking-wide text-[#8B8A8E] md:table-cell">Plattform</TableHead>
              <TableHead className="hidden text-xs font-bold uppercase tracking-wide text-[#8B8A8E] md:table-cell">Keyword</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide text-[#8B8A8E]">KI</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide text-[#8B8A8E]">Status</TableHead>
              <TableHead className="px-6 text-right text-xs font-bold uppercase tracking-wide text-[#8B8A8E]">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleCases.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-16 text-center text-sm text-[#8B8A8E]">
                  Keine Fälle in dieser Ansicht.
                </TableCell>
              </TableRow>
            )}
            {visibleCases.map((c) => (
              <TableRow
                key={c.id}
                data-testid={`case-row-${c.id}`}
                className="cursor-pointer border-[#EDE9E3] transition-colors hover:bg-[#FAF7F2]"
                onClick={() => openCase(c)}
              >
                <TableCell className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#E8E4DF] bg-[#F4F1EC] font-heading text-sm font-bold text-[#0A0A0A]">
                      {c.profile_name?.charAt(0) || "?"}
                      {c.is_new && <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-[#0A0A0A]" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#0A0A0A]">{c.profile_name}</span>
                        {c.is_new && (
                          <span data-testid={`new-badge-${c.id}`} className="inline-flex items-center gap-1 rounded-sm bg-[#0A0A0A] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                            <Sparkles className="h-2.5 w-2.5" /> Neu
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#8B8A8E]">
                        {c.handle}
                        <span className="md:hidden">{c.handle ? " · " : ""}{PLATFORM_LABELS[c.platform]}</span>
                      </div>
                      {c.profile_url && (
                        <a
                          href={c.profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`case-link-${c.id}`}
                          className="mt-0.5 inline-flex max-w-[260px] items-center gap-1 truncate text-xs font-medium text-[#0A0A0A] underline decoration-[#D8D3CC] underline-offset-2 hover:text-[#A6A4A9] sm:max-w-[420px]"
                          title={c.profile_url}
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          <span className="truncate">{c.profile_url.replace(/^https?:\/\/(www\.)?/, "")}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden text-sm font-medium text-[#3A3A3C] md:table-cell">{PLATFORM_LABELS[c.platform]}</TableCell>
                <TableCell className="hidden text-sm text-[#3A3A3C] md:table-cell">{c.matched_keyword}</TableCell>
                <TableCell>
                  <span className={`inline-flex rounded-sm px-2 py-0.5 text-sm font-bold tabular-nums ${c.ai_confidence >= 70 ? "bg-rose-50 text-rose-600" : "bg-[#F4F1EC] text-[#8B8A8E]"}`}>
                    {c.ai_confidence}%
                  </span>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Select value={c.status} onValueChange={(v) => changeStatus(c.id, v)}>
                    <SelectTrigger
                      data-testid={`status-select-${c.id}`}
                      className="h-auto w-auto border-none bg-transparent p-0 shadow-none focus:ring-0"
                    >
                      <StatusBadge status={c.status} testId={`status-badge-${c.id}`} />
                    </SelectTrigger>
                    <SelectContent className="rounded-md">
                      {STATUS_FLOW.map((s) => (
                        <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="px-6 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {c.profile_url && (
                      <a href={c.profile_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-md text-[#A6A4A9] hover:bg-[#F4F1EC] hover:text-[#0A0A0A]">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </a>
                    )}
                    <Button
                      data-testid={`delete-case-${c.id}`}
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-md text-[#A6A4A9] hover:bg-rose-50 hover:text-rose-600"
                      onClick={() => remove(c.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <CaseDetail
        caseItem={selected}
        onClose={() => setSelected(null)}
        onUpdated={() => {
          load();
        }}
      />
    </div>
  );
}
